import { apiClient } from './api'
import type { User, UserCreateDto, UserUpdateDto, UserPreferencesUpdate, LinkedAccountDetail } from '@/types/user.types'
import { mapUserFromApi } from '@/utils/userMapper'

export interface UserListResponse {
  users: User[]
  total: number
}

export const userService = {
  getUsers: async (activeOnly?: boolean): Promise<UserListResponse> => {
    const { data } = await apiClient.get('/users', { params: { activeOnly } })
    return {
      users: data.users.map(mapUserFromApi),
      total: data.total
    }
  },

  getUser: async (userId: string): Promise<User> => {
    const { data } = await apiClient.get(`/users/${userId}`)
    return mapUserFromApi(data)
  },

  createUser: async (user: UserCreateDto): Promise<User> => {
    const { data } = await apiClient.post('/users', user)
    return mapUserFromApi(data)
  },

  updateUser: async (userId: string, user: UserUpdateDto): Promise<User> => {
    const { data} = await apiClient.put(`/users/${userId}`, user)
    return mapUserFromApi(data)
  },

  updateUserSettings: async (userId: string, preferences: UserPreferencesUpdate): Promise<User> => {
    const { data } = await apiClient.put(`/users/${userId}/settings`, preferences)
    return mapUserFromApi(data)
  },

  deleteUser: async (userId: string): Promise<void> => {
    await apiClient.delete(`/users/${userId}`)
  },

  sendPasswordReset: async (userId: string): Promise<void> => {
    await apiClient.post(`/users/${userId}/password-reset`)
  },

  uploadProfilePhoto: async (userId: string, file: File): Promise<{ avatarUrl: string }> => {
    const formData = new FormData()
    formData.append('photo', file)
    const { data } = await apiClient.post(`/users/${userId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    return data
  },

  getUserLinkedAccounts: async (userId: string): Promise<LinkedAccountDetail[]> => {
    const { data } = await apiClient.get(`/users/${userId}/linked-accounts`)
    return data.map((a: any) => ({
      id: a.id,
      name: a.name,
      isDefault: a.is_default ?? false,
      isAdmin: a.is_admin ?? false,
    }))
  }
}
